<template>
  <fragment>
    <!-- HINT --->
    <template v-if="t.type !== 'wildcard' && t.type !== 'deepwild'">
      <div class="header">{{ $t('MATCH_HINT_MENU_HEADER') }}</div>

      <div class="ui dropdown item" @click="toggleHintMenu">
        {{ currentTokenHintI18n }}
        <i class="dropdown icon"></i>
        <div
          class="ui transitioning menu"
          :style="{ display: showMatchHintMenu ? 'block !important' : 'none' }"
          :class="{ visible: showMatchHintMenu }"
        >
          <div
            class="item"
            v-for="hint in matchHintOptions"
            :class="{ active: hint.id === t.hint }"
            :key="hint.id"
            @click="updateHint(hint)"
          >
            {{ hint.name }}
          </div>
        </div>
      </div>
    </template>

    <!-- MATCH --->
    <div class="header">{{ $t('MATCH_TYPE_MENU_HEADER') }}</div>
    <div class="ui dropdown item" @click="toggleMatchTypeMenu">
      {{ currentTokenTypeI18n }}
      <i class="dropdown icon"></i>
      <div
        class="ui transitioning menu"
        :style="{ display: showMatchTypeMenu ? 'block !important' : 'none' }"
        :class="{ visible: showMatchTypeMenu }"
      >
        <div
          class="item"
          v-for="type in applicableMatchTypes"
          :class="{ active: type.id === t.type }"
          :key="type.id"
          @click="updateTokenType(type)"
        >
          {{ type.name }}
        </div>
      </div>
    </div>

    <div class="item" v-if="t">
      <div class="two fields">
        <div class="twelve wide field">
          <label for="matchText">{{ $t('MATCH_TEXT') }}</label>
          <div v-if="t.type === 'regex'" ref="matchText">
            <regex-field
              v-model.trim="t.text"
              @input="updateMatchText"
              size="mini"
              @action="handleRegexAction"
              :actions="regexActions"
            />
          </div>
          <div
            v-else
            ref="matchText"
            class="ui mini action input"
            :class="{ disabled: t.type === 'wildcard' || t.type === 'deepwild' }"
          >
            <input
              id="matchText"
              name="matchText"
              type="text"
              v-model="t.text"
              @keyup="emitTokenUpdate()"
            />

            <!-- Button for resolvedProperties -->
            <button
              class="ui mini icon button"
              @click.prevent.stop="toggleMatchTextOptions('matchText', 'matchTextOptionsMenu')"
              v-if="resolvedProperties && resolvedProperties.length > 0"
            >
              <i class="ellipsis horizontal icon" />
            </button>
            <button
              class="ui mini green icon button"
              :class="{
                loading: monitoring,
                disabled: t.type === 'wildcard' || t.type === 'deepwild'
              }"
              @click.prevent.stop="resolveSingle()"
            >
              <i class="crosshairs icon" />
            </button>
          </div>
          <div
            ref="matchTextOptionsMenu"
            class="menu transition"
            :class="{ visible: showMatchTextOptions, hidden: !showMatchTextOptions }"
            :style="[showMatchTextOptions ? 'display: block !important;' : 'display: none;']"
          >
            <div
              class="item"
              v-for="p in resolvedProperties"
              :key="p.name"
              @click="updateMatchText(p.value, p.hint)"
            >
              <i class="fingerprint icon" :class="{ green: p.unique }" />
              {{ p.value }}
              <div class="description">{{ p.name }}</div>
            </div>
          </div>
        </div>

        <div class="four wide field">
          <label for="matchIndex">{{ $t('MATCH_INDEX') }}</label>
          <div class="ui mini input" :class="{ disabled: t.type == 'deepwild' }">
            <input
              id="matchIndex"
              name="matchIndex"
              type="number"
              class="no-spin"
              v-model.number="t.index"
              @keyup="emitTokenUpdate()"
            />
          </div>
        </div>
      </div>
    </div>
  </fragment>
</template>

<style lang="scss">
.no-spin::-webkit-inner-spin-button,
.no-spin::-webkit-outer-spin-button {
  -webkit-appearance: none !important;
  margin: 0 !important;
}

.no-spin {
  -moz-appearance: textfield !important;
}
</style>

<script>
// @flow
import Vue from 'vue'
import _ from 'lodash'
import { Fragment } from 'vue-frag'
import { RegexField } from 'ui'
import { MonitorSocket } from 'services'
import parser from './Path.pegjs'

const hintAndValueRegex = /^(\(([^)]+)\))?(.+)$/

export default {
  name: 'SingleTokenMenuFragment',
  props: ['token', 'resolved', 'app', 'inTail'],

  components: {
    Fragment,
    RegexField
  },

  data() {
    return {
      t: _.cloneDeep(this.token),
      showMatchTypeMenu: false,
      showMatchHintMenu: false,
      showMatchTextOptions: false,
      matchTypeOptions: [
        { name: this.$i18n.t('MATCH_TYPE_EXACT'), id: 'exact' },
        { name: this.$i18n.t('MATCH_TYPE_REGEX'), id: 'regex' },
        { name: this.$i18n.t('MATCH_TYPE_STARTSWITH'), id: 'startswith' },
        { name: this.$i18n.t('MATCH_TYPE_ENDSWITH'), id: 'endswith' },
        { name: this.$i18n.t('MATCH_TYPE_CONTAINS'), id: 'contains' },
        { name: this.$i18n.t('MATCH_TYPE_DEEPWILD'), id: 'deepwild', hide: this.inTail },
        { name: this.$i18n.t('MATCH_TYPE_WILDCARD'), id: 'wildcard' }
      ],
      matchHintOptions: [
        { name: this.$i18n.t('MATCH_HINT_ALL'), id: null },
        { name: this.$i18n.t('MATCH_HINT_TEXT'), id: 'text' },
        { name: this.$i18n.t('MATCH_HINT_ID'), id: 'id' },
        { name: this.$i18n.t('MATCH_HINT_TYPE'), id: 'type' },
        { name: this.$i18n.t('MATCH_HINT_CLASS'), id: 'class' },
        { name: this.$i18n.t('MATCH_HINT_NAME'), id: 'name' },
        { name: this.$i18n.t('MATCH_HINT_LEGACYVALUE'), id: 'lvalue' }
      ],
      ms: null,
      monitoring: false,
      monitorResolved: null
    }
  },

  computed: {
    regexActions() {
      return _.filter([
        this.resolved ? { name: 'menu', icon: 'ellipsis horizontal' } : null,
        { name: 'monitor', icon: 'crosshairs', color: 'green' }
      ])
    },
    currentTokenTypeI18n() {
      return this.matchTypeOptions.find(type => type.id === this.t?.type)?.name
    },
    currentTokenHintI18n() {
      return this.matchHintOptions.find(hint => hint.id === this.t?.hint)?.name || this.t?.hint
    },
    actualResolved() {
      return this.monitorResolved ?? this.resolved
    },
    resolvedProperties() {
      return _.filter(
        _.map(_.omit(this.actualResolved, ['uniqueTokens']), (value, key) => ({
          ...this.hintAndValue(value),
          name: key,
          rawValue: value,
          unique: _.includes(this.actualResolved.uniqueTokens, value)
        })),
        p => p.value
      )
    },
    applicableMatchTypes() {
      return _.filter(this.matchTypeOptions, type => !type.hide)
    }
  },

  watch: {
    token() {
      this.t = _.cloneDeep(this.token)
    }
  },

  methods: {
    hintAndValue(v) {
      const m = hintAndValueRegex.exec(v)
      return m ? { hint: m[2], value: m[3] } : v
    },
    handleRegexAction(action) {
      console.debug('handleRegexAction', action)
      switch (action.name) {
        case 'menu':
          this.toggleMatchTextOptions('matchText', 'matchTextOptionsMenu')
          break
        case 'monitor':
          this.startFieldDiscovery('byfield', 'path')
          break
      }
    },
    emitMonitorResolved() {
      this.$emit('monitor-resolved', this.monitorResolved)
    },
    emitTokenUpdate() {
      this.$emit('token-update', this.t)
    },
    updateTokenType(type) {
      this.t.type = type.id
      this.emitTokenUpdate()
    },
    updateHint(hint) {
      this.t.hint = hint.id
      this.emitTokenUpdate()
    },
    toggleMatchTextOptions(targetId, menuRef) {
      this.showMatchTextOptions = !this.showMatchTextOptions

      // No need to place the menu below the input if we're closing it
      if (!this.showMatchTextOptions) return

      // place menu below the evt target
      const menu = this.$refs[menuRef]
      const target = this.$refs[targetId]
      menu.style.position = 'absolute'
      menu.style.left = target.offsetLeft + 7 + 'px'
      menu.style.top = target.offsetTop + 35 + 'px'
    },
    toggleMatchTypeMenu() {
      this.showMatchTypeMenu = !this.showMatchTypeMenu
      this.showMatchHintMenu = false
    },
    toggleHintMenu() {
      this.showMatchHintMenu = !this.showMatchHintMenu
      this.showMatchTypeMenu = false
    },

    updateMatchText(value, hint) {
      this.t.text = value
      if (hint) this.t.hint = hint
      this.emitTokenUpdate()
      this.showMatchTextOptions = false
    },

    resolveSingle() {
      this.startFieldDiscovery('byfield', 'path')
    },

    startFieldDiscovery(mode, pathOrImage) {
      if (this.t?.type === 'deepwild') return
      const updateAndEmit = fc => {
        try {
          const tokens = parser.parse(fc.Path)
          // Take the target from the tail token
          this.t = _.last(tokens).target
          this.monitorResolved = _.last(fc.PathInfo)
          this.emitTokenUpdate()
          this.emitMonitorResolved()
        } catch (err) {
          console.error('Error parsing monitored path', fc.path, err)
        }
      }

      this.ms = MonitorSocket()
      this.monitoring = false
      this.ms.on('open', () => {
        this.ms.startMonitor(this.app, mode)
      })
      this.ms.on('selected', fc => {
        updateAndEmit(fc)
        this.cleanupMonitorSession()
      })
      this.ms.on('hover', updateAndEmit)

      this.ms.on('initialized', () => (this.monitoring = true))
    },

    cleanupMonitorSession() {
      if (this.ms) {
        this.ms.remove()
        this.ms = null
      }
      this.monitoring = false
    }
  }
}
</script>
